import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Loader } from "semantic-ui-react";
import { selectCurrentToken } from "../auth/authSlice";
import { uploadFileToS3Presigned } from "../../utils/presignedUpload";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { shortDate } from "../chat_sessions/MobileChatSessionList";
import { Attachment } from "./supportTicketChrome";
import {
  DetailPageShell, FilePick, FormSection, ListNote, MonoBlock, Tag, dangerPill, listCard, pillButton, stack, textareaStyle,
} from "../../components/listPage";
import {
  useCloseSupportTicketMutation,
  useCreateSupportReplyMutation,
  useGetSupportTicketByIdQuery,
  useGetSupportTicketImageUrlQuery,
} from "./supportTicketApiSlice";
import { getSupportTicketStatusLabel, isReporterReply } from "./supportTicketUtils";

/**
 * 지원 요청 상세, in the same visual language as the list and 대화 내역.
 *
 * The conversation itself keeps `.message-row` / `.message-bubble` from index.css — the same
 * classes the roleplay transcript uses. Those were already the right language for a thread
 * of messages, so they are untouched; everything around them is what read as a different
 * product: `<Header as="h2">`, three stacked `<Segment>`s and Semantic form controls.
 *
 * DetailPageShell's reading width, not the lists' full width: a thread plus a compose box,
 * and full-bleed 16px text across 1600px is not readable.
 */
const AdminSupportTicketDetailPage = () => {
  const { id } = useParams();
  const { isMobile } = useBreakpoint();
  const token = useSelector(selectCurrentToken);
  const [replyBody, setReplyBody] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(imageFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  const { data: ticket, isLoading, isError } = useGetSupportTicketByIdQuery(id);

  const { data: imageData, isLoading: isImageLoading } = useGetSupportTicketImageUrlQuery(
    id,
    { skip: !ticket?.image_s3_key }
  );

  const [createSupportReply, { isLoading: isReplying }] = useCreateSupportReplyMutation();
  const [closeSupportTicket, { isLoading: isClosing }] = useCloseSupportTicketMutation();

  const isClosed = ticket?.status === "closed";
  const busy = isReplying || isClosing || isUploading;

  const handleReplySubmit = async () => {
    if (!replyBody.trim()) {
      setError("답변 내용을 입력해 주세요.");
      return;
    }

    setError(null);
    let imageS3Key = null;
    if (imageFile) {
      setIsUploading(true);
      const uploadResult = await uploadFileToS3Presigned(imageFile, token);
      setIsUploading(false);
      if (!uploadResult.success) {
        setError(uploadResult.error || "이미지 업로드에 실패했습니다.");
        return;
      }
      imageS3Key = uploadResult.key;
    }

    const result = await createSupportReply({
      ticketId: id,
      body: replyBody.trim(),
      ...(imageS3Key ? { image_s3_key: imageS3Key } : {}),
    });
    if (result.error) {
      setError("답변 등록에 실패했습니다.");
      return;
    }
    setReplyBody("");
    setImageFile(null);
  };

  const handleClose = async () => {
    setError(null);
    const result = await closeSupportTicket(id);
    if (result.error) {
      setError("요청 종료에 실패했습니다.");
    }
  };

  if (isLoading) return <Loader active />;

  const status = ticket && getSupportTicketStatusLabel(ticket.status);
  const replies = ticket?.replies || [];
  const author = ticket?.author?.full_name || ticket?.author?.username;

  return (
    <DetailPageShell
      isMobile={isMobile}
      title="지원 요청 상세"
      subtitle={ticket ? `${author} · ${shortDate(ticket.created_at)}` : null}
      backTo="/admin/support-tickets"
      actions={status && <Tag tone={status.tone}>{status.text}</Tag>}
    >
      {isError || !ticket ? (
        <div style={{ ...listCard, textAlign: 'center', color: '#86868b' }}>요청을 불러오지 못했습니다.</div>
      ) : (
        <div style={stack}>
          <div style={{ ...listCard, padding: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="message-row received">
                <div className="message-bubble received">
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{author}</div>
                  <p className="message-text">{ticket.body}</p>
                  <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>{shortDate(ticket.created_at)}</div>
                </div>
              </div>
              {ticket.image_s3_key && (
                <div style={{ margin: '12px 0', maxWidth: '70%' }}>
                  {isImageLoading && <Loader active inline size="small" />}
                  {imageData?.url && <Attachment url={imageData.url} />}
                </div>
              )}
              {replies.map(reply => {
                const side = isReporterReply(reply, ticket) ? 'received' : 'sent';
                return (
                  <div key={reply.id} className={`message-row ${side}`}>
                    <div className={`message-bubble ${side}`}>
                      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{reply.author?.full_name || reply.author?.username}</div>
                      <p className="message-text">{reply.body}</p>
                      {reply.image_url && <Attachment url={reply.image_url} style={{ marginTop: 8 }} />}
                      <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>{shortDate(reply.created_at)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {isClosed ? (
            <ListNote tone="green">종료된 요청입니다. 추가 답변 및 종료 작업을 할 수 없습니다.</ListNote>
          ) : (
            <FormSection title="답변 작성">
              <div style={stack}>
                <textarea style={textareaStyle} placeholder="사용자에게 보낼 답변을 입력해 주세요." value={replyBody} onChange={e => setReplyBody(e.target.value)} />
                <FilePick accept="image/jpeg,image/png,.jpg,.jpeg,.png" file={imageFile} onChange={f => { setImageFile(f); setError(null); }} />
                {previewUrl && <img src={previewUrl} alt="첨부 미리보기" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 12, display: 'block' }} />}
                {error && <ListNote tone="red">{error}</ListNote>}
                {/* 요청 종료 is pushed away from 답변 등록: they sit side by side and one of them
                    is irreversible. */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button type="button" disabled={busy} onClick={handleReplySubmit} style={{ ...pillButton(true), opacity: busy ? 0.5 : 1 }}>
                    {isUploading ? '이미지 업로드 중…' : isReplying ? '등록 중…' : '답변 등록'}
                  </button>
                  <button type="button" disabled={busy} onClick={handleClose} style={{ ...dangerPill, marginLeft: 'auto', opacity: busy ? 0.5 : 1 }}>
                    {isClosing ? '종료 중…' : '요청 종료'}
                  </button>
                </div>
              </div>
            </FormSection>
          )}

          {/* Kept as raw JSON: it is whatever the reporting screen attached, and an admin
              debugging a report wants it verbatim. Below the thread: it's for debugging. */}
          {ticket.context && Object.keys(ticket.context).length > 0 && (
            <FormSection title="컨텍스트">
              <MonoBlock>{JSON.stringify(ticket.context, null, 2)}</MonoBlock>
            </FormSection>
          )}
        </div>
      )}
    </DetailPageShell>
  );
};

export default AdminSupportTicketDetailPage;
